import { DAYS_PER_WEEK, MAX_CALENDAR_YEAR, MIN_CALENDAR_YEAR, addDays } from '../date/index.js';
import { isoWeekOf, isoWeeksInYear, startOfIsoWeekYear } from './iso-week.js';
/**
 * Builds the 52- or 53-column calendar-week grid for `planningYear`.
 *
 * Columns are contiguous, ordered, non-overlapping and seven days wide by
 * construction: each one starts a week after its predecessor, counted from the
 * Monday of week 1.
 */
export function buildIsoWeekGrid(planningYear) {
    if (!Number.isInteger(planningYear)) {
        throw new RangeError(`planning year must be an integer, received ${String(planningYear)}`);
    }
    if (planningYear < MIN_CALENDAR_YEAR || planningYear > MAX_CALENDAR_YEAR) {
        throw new RangeError(`planning year must be ${String(MIN_CALENDAR_YEAR)}-${String(MAX_CALENDAR_YEAR)}, received ${String(planningYear)}`);
    }
    const weekCount = isoWeeksInYear(planningYear);
    const firstMonday = startOfIsoWeekYear(planningYear);
    const columns = [];
    for (let index = 0; index < weekCount; index += 1) {
        const start = addDays(firstMonday, index * DAYS_PER_WEEK);
        columns.push({
            index,
            isoYear: planningYear,
            isoWeek: index + 1,
            start,
            end: addDays(start, DAYS_PER_WEEK - 1),
        });
    }
    const lastColumn = columns[weekCount - 1];
    if (lastColumn === undefined) {
        // Unreachable: `weekCount` is 52 or 53, so the loop always pushes columns.
        throw new RangeError(`ISO year ${String(planningYear)} produced no week columns`);
    }
    return {
        planningYear,
        weekCount,
        columns,
        start: firstMonday,
        end: lastColumn.end,
    };
}
/**
 * The column carrying `date`, or `undefined` when `date` belongs to a
 * neighbouring ISO year and therefore to a neighbouring year's grid.
 *
 * Membership is decided by the date's own ISO week reference rather than by a
 * range comparison, so the answer agrees with {@link isoWeekOf} by construction.
 */
export function findIsoWeekColumn(grid, date) {
    const reference = isoWeekOf(date);
    if (reference.isoYear !== grid.planningYear)
        return undefined;
    return grid.columns[reference.isoWeek - 1];
}
/** Whether `date` falls in one of the grid's columns. */
export function isoWeekGridContains(grid, date) {
    return findIsoWeekColumn(grid, date) !== undefined;
}
/** The column with week number `isoWeek`, or `undefined` when the grid has no such week. */
export function isoWeekColumnByNumber(grid, isoWeek) {
    if (!Number.isInteger(isoWeek) || isoWeek < 1)
        return undefined;
    return grid.columns[isoWeek - 1];
}
//# sourceMappingURL=iso-week-grid.js.map